/**
 * TermsAcceptanceCard (OCP-525) - which Terms version this account accepted,
 * when, by whom, plus the full decision history and the notice state. Lets
 * the operator record a decision that arrived out of band (a customer's
 * objection or acceptance by mail), with the mandatory reference.
 *
 * Self-fetching from `/v1/admin/accounts/:id/terms` so the account page's
 * main load and this card fail independently.
 *
 * OCP-847 - both documents: the AGB and the AVV (Art. 28 GDPR) with their
 * current and accepted versions; every history row names its document, the
 * order the click placed and (AGB rows) what the click did with the AVV.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError, readErrorKey } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

type TermsKind = 'agb' | 'avv';

interface VersionSummary {
  id: string;
  kind?: TermsKind;
  version: string;
  title: string;
  effective_from: string;
  current: boolean;
}

interface AcceptanceRecord {
  id: string;
  version: string;
  decision: 'accepted' | 'rejected';
  context: string;
  decided_at: string;
  actor_email: string;
  notice_reference: string | null;
  ip_address: string | null;
  /** OCP-847 */
  kind?: TermsKind;
  bundle_id?: string | null;
  order_id?: string | null;
  avv_state?: 'recorded' | 'already_accepted' | 'not_published' | null;
}

interface KindStatus {
  kind: TermsKind;
  state: TermsAdminStatus['state'];
  current_version: VersionSummary | null;
  accepted_version: VersionSummary | null;
  accepted_at: string | null;
  notice: { sent_at: string; mail_id: string | null; mail_error: string | null } | null;
}

interface TermsAdminStatus {
  state: 'accepted' | 'pending' | 'rejected' | 'none' | 'unpublished';
  current_version: VersionSummary | null;
  accepted_version: VersionSummary | null;
  accepted_at: string | null;
  notice: { sent_at: string; mail_id: string | null; mail_error: string | null } | null;
  history: AcceptanceRecord[];
  /** OCP-847 - the AVV next to the AGB; absent on an API from before OCP-847. */
  avv?: KindStatus;
  can_conclude?: boolean;
}

const KIND_LABEL: Record<TermsKind, string> = { agb: 'AGB', avv: 'AVV' };

const AVV_STATE_LABEL: Record<NonNullable<AcceptanceRecord['avv_state']>, string> = {
  recorded: 'AVV recorded with this click',
  already_accepted: 'AVV accepted before',
  not_published: 'no AVV published at the click',
};

const STATE_LABEL: Record<TermsAdminStatus['state'], string> = {
  accepted: 'Accepted',
  pending: 'Decision pending',
  rejected: 'Objected',
  none: 'Never decided',
  unpublished: 'No version published',
};

function stateClasses(state: TermsAdminStatus['state']): string {
  if (state === 'accepted') return 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300';
  if (state === 'rejected') return 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300';
  return 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300';
}

function fmt(iso: string | null): string {
  return iso ? fmtDateTime(iso) : '-';
}

export function TermsAcceptanceCard({ customerId }: { customerId: string }) {
  const [data, setData] = useState<TermsAdminStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [decision, setDecision] = useState<'accepted' | 'rejected'>('accepted');
  const [decisionKind, setDecisionKind] = useState<TermsKind>('agb');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api<TermsAdminStatus>(`/v1/admin/accounts/${customerId}/terms`));
    } catch (err) {
      setError(err instanceof ApiError ? `Could not load the Terms state (HTTP ${err.status}).` : 'Could not load the Terms state.');
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const decisionVersion = decisionKind === 'avv' ? (data?.avv?.current_version ?? null) : (data?.current_version ?? null);

  async function record() {
    if (!decisionVersion) return;
    setBusy(true);
    setSaveError(null);
    try {
      const next = await api<TermsAdminStatus>(`/v1/admin/accounts/${customerId}/terms/decision`, {
        method: 'POST',
        body: { version_id: decisionVersion.id, decision, notice_reference: reference.trim() },
      });
      setData(next);
      setRecording(false);
      setReference('');
    } catch (err) {
      // OCP-847 - the AVV is mandatory: with none published nothing is recorded (409).
      const key = err instanceof ApiError ? readErrorKey(err.body) : null;
      setSaveError(
        key === 'avv_not_published'
          ? 'No AVV (data processing agreement) version is published; publish one first - no decision can be recorded without it.'
          : err instanceof ApiError
            ? `Could not record (HTTP ${err.status}).`
            : 'Could not record.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm" data-testid="terms-acceptance-card">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Terms acceptance</h2>
        {data && (
          <span className={`rounded px-2 py-0.5 text-[10px] font-semibold uppercase ${stateClasses(data.state)}`} data-testid="terms-card-state">
            {STATE_LABEL[data.state]}
          </span>
        )}
      </div>

      {error && (
        <div role="alert" className="mt-3 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {data && (
        <>
          <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
            <dt className="text-gray-500">Current version</dt>
            <dd className="text-gray-900 dark:text-gray-100">{data.current_version ? `${data.current_version.version} (effective ${fmt(data.current_version.effective_from)})` : '-'}</dd>
            <dt className="text-gray-500">Accepted version</dt>
            <dd className="text-gray-900 dark:text-gray-100" data-testid="terms-card-accepted">
              {data.accepted_version ? `${data.accepted_version.version} on ${fmt(data.accepted_at)}` : 'none'}
            </dd>
            <dt className="text-gray-500">Change notice</dt>
            <dd className="text-gray-900 dark:text-gray-100">
              {data.notice ? `${fmt(data.notice.sent_at)}${data.notice.mail_id ? ` · ${data.notice.mail_id}` : ' · send failed'}` : 'not sent for the current version'}
            </dd>
            {data.avv && (
              <>
                <dt className="text-gray-500">AVV (Art. 28 GDPR)</dt>
                <dd className="text-gray-900 dark:text-gray-100" data-testid="terms-card-avv">
                  {data.avv.state === 'unpublished'
                    ? 'no AVV version published'
                    : `${STATE_LABEL[data.avv.state]} · current ${data.avv.current_version?.version ?? '-'} · accepted ${
                        data.avv.accepted_version ? `${data.avv.accepted_version.version} on ${fmt(data.avv.accepted_at)}` : 'none'
                      }`}
                </dd>
              </>
            )}
            {data.can_conclude !== undefined && (
              <>
                <dt className="text-gray-500">May conclude a contract</dt>
                <dd className="text-gray-900 dark:text-gray-100" data-testid="terms-card-can-conclude">{data.can_conclude ? 'yes' : 'no'}</dd>
              </>
            )}
          </dl>

          {data.history.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">History</div>
              <ul className="mt-1 divide-y divide-gray-100 dark:divide-gray-800 text-xs" data-testid="terms-card-history">
                {data.history.map((h) => (
                  <li key={h.id} className="flex flex-wrap justify-between gap-2 py-1">
                    <span>
                      <span className={h.decision === 'accepted' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>{h.decision}</span>{' '}
                      <span className="font-semibold">{KIND_LABEL[h.kind ?? 'agb']}</span> {h.version} · {h.context} · {h.actor_email}
                      {h.order_id ? ` · order ${h.order_id.slice(0, 8)}` : ''}
                      {h.avv_state ? ` · ${AVV_STATE_LABEL[h.avv_state]}` : ''}
                      {h.notice_reference ? ` · ref: ${h.notice_reference}` : ''}
                      {h.ip_address ? ` · ${h.ip_address}` : ''}
                    </span>
                    <span className="tabular-nums text-gray-500">{fmt(h.decided_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(data.current_version || data.avv?.current_version) && (
            <div className="mt-3">
              {!recording ? (
                <button
                  type="button"
                  onClick={() => setRecording(true)}
                  data-testid="terms-card-record"
                  className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Record a decision received in text form
                </button>
              ) : (
                <div className="rounded border border-gray-200 dark:border-gray-800 p-3">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    For a decision on {KIND_LABEL[decisionKind]} version {decisionVersion?.version ?? '-'} that reached us by mail or letter. The
                    reference is the evidence - mandatory.
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                    {data.avv?.current_version && (
                      <select value={decisionKind} onChange={(e) => setDecisionKind(e.target.value as TermsKind)} data-testid="terms-card-kind" className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm">
                        <option value="agb">AGB</option>
                        <option value="avv">AVV</option>
                      </select>
                    )}
                    <select value={decision} onChange={(e) => setDecision(e.target.value as 'accepted' | 'rejected')} className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm">
                      <option value="accepted">accepted</option>
                      <option value="rejected">objected / rejected</option>
                    </select>
                    <input
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder='e.g. "Mail from owner@…, 2026-09-05, subject AGB v2"'
                      data-testid="terms-card-reference"
                      className="min-w-[16rem] flex-1 rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
                    />
                  </div>
                  {saveError && <p role="alert" className="mt-2 text-xs text-red-700 dark:text-red-300">{saveError}</p>}
                  <div className="mt-2 flex justify-end gap-2">
                    <button type="button" onClick={() => setRecording(false)} disabled={busy} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs text-gray-700 dark:text-gray-300">
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => void record()}
                      disabled={busy || reference.trim().length < 5 || !decisionVersion}
                      data-testid="terms-card-save"
                      className="rounded bg-brand-500 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                    >
                      {busy ? 'Recording…' : 'Record'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
